/**
 * `useAppRole` — одна семантика роли на всю админку (B-2175).
 *
 * `usePermissions` (→ `authProvider.getPermissions`) даёт ТРИ разных состояния,
 * и раньше `RoleGuard` и `MentorSider` трактовали их по-разному:
 *
 * | data          | когда                                             | было в RoleGuard | было в MentorSider |
 * |---------------|---------------------------------------------------|------------------|--------------------|
 * | `undefined`   | пока грузится `/v1/auth/me`                       | Spin             | ПОЛНОЕ меню        |
 * | `null`        | `getPermissions` проглотил ошибку (таймаут/5xx)   | deny             | ПОЛНОЕ меню        |
 * | `"admin"`/`"mentor"` | роль известна                              | по `allow`       | по роли            |
 *
 * Из-за этого ментор видел админское меню при каждом F5 и до конца сессии при
 * отказе `/v1/auth/me`. Здесь три состояния сводятся к одному типу, и оба
 * компонента обязаны разобрать его целиком — «показать всё» больше не является
 * значением по умолчанию.
 *
 * `denied` — это и «роли нет», и «роль неизвестна из-за ошибки»: различать их
 * незачем, реакция одна — прав не выдаём (fail-closed).
 */

import { usePermissions } from "@refinedev/core";

import type { AppRole } from "../lib/panel-access";

export type RoleState = AppRole | "loading" | "denied";

export function useAppRole(): RoleState {
  const { data, isLoading, isFetching } = usePermissions<AppRole | null>();

  // react-query v4: `isLoading` = «данных ещё нет», в т.ч. у выключенного
  // запроса — тот висел бы в loading вечно. `isLoading && isFetching` — это
  // «первая загрузка идёт прямо сейчас»; выключенный запрос сразу даёт denied.
  if (isLoading && isFetching) return "loading";

  if (data === "admin" || data === "mentor") return data;
  return "denied";
}
